/**
 * Withdrawal Modal Manager
 * 
 * Handles opening, closing, and state management for the wallet withdrawal modal.
 * Follows Single Responsibility Principle - only manages modal UI state.
 * 
 * @package betbeast
 */

import { getWalletRedirectUrl } from '../account-redirect-utils.js';

export class WithdrawalModal {
    constructor(modalId = 'wallet-withdrawal-modal') {
        this.modal = document.getElementById(modalId);
        this.modalClass = 'wallet-withdrawal-modal';
        this.bodyClass = 'wallet-withdrawal-modal-open';
        this.isOpen = false;
        
        if (!this.modal) {
            console.warn(`Withdrawal modal with ID "${modalId}" not found`);
            return;
        }
        
        this.init();
    }
    
    /**
     * Initialize modal event listeners
     */
    init() {
        // Close button handlers
        const closeButtons = this.modal.querySelectorAll('[data-wallet-withdrawal-modal-close]');
        closeButtons.forEach(button => {
            button.addEventListener('click', () => this.close());
        });
        
        // Overlay click to close
        const overlay = this.modal.querySelector('.wallet-withdrawal-modal__overlay');
        if (overlay) {
            overlay.addEventListener('click', () => this.close());
        }
        
        // Escape key to close
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });

        // Balance info tooltip handler
        this.initBalanceTooltip();
    }

    /**
     * Initialize balance info tooltip
     */
    initBalanceTooltip() {
        const infoIcon = this.modal.querySelector('[data-balance-info-trigger]');
        const tooltip = this.modal.querySelector('[data-balance-info-tooltip]');
        
        if (!infoIcon || !tooltip) return;

        let isTooltipVisible = false;

        // Toggle tooltip on click
        infoIcon.addEventListener('click', (e) => {
            e.stopPropagation();
            isTooltipVisible = !isTooltipVisible;
            tooltip.style.display = isTooltipVisible ? 'block' : 'none';
        });

        // Close tooltip when clicking outside
        document.addEventListener('click', (e) => {
            if (isTooltipVisible && !tooltip.contains(e.target) && e.target !== infoIcon) {
                isTooltipVisible = false;
                tooltip.style.display = 'none';
            }
        });

        // Close tooltip on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && isTooltipVisible) {
                isTooltipVisible = false;
                tooltip.style.display = 'none';
            }
        });
    }
    
    /**
     * Open the modal
     */
    open() {
        if (!this.modal) return;
        
        this.modal.classList.add('is-open');
        this.modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add(this.bodyClass);
        this.isOpen = true;
        
        // Focus management for accessibility
        const firstFocusable = this.modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }
    
    /**
     * Close the modal
     */
    close() {
        if (!this.modal) return;
        
        this.modal.classList.remove('is-open');
        this.modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove(this.bodyClass);
        this.isOpen = false;

        // Redirect to wallet page on close
        window.location.href = getWalletRedirectUrl();
    }
    
    /**
     * Check if modal is currently open
     */
    isModalOpen() {
        return this.isOpen;
    }
}
